import { fetchEventSource } from '@microsoft/fetch-event-source';
import { axiosApiInstance } from 'Store';
import { ChatBotMessageDto, ChatDetailDto, ChatPageDto, MessageResponseDto, SuggestedQuestionCategoryDto, SuggestedQuestionRequestDto } from 'utils/types/ChatbotModels';

import { activeNotification } from 'components/functions/activeNotification';

const getConversationHistory = async (): Promise<ChatPageDto | null> => {
    return axiosApiInstance
        .get('/chatbot/history')
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }

            return {
                oldestChat: [],
                newestChat: null
            };
        })
        .catch((error) => {
            if (error.response?.status === 404) {
                return {
                    oldestChat: [],
                    newestChat: null
                };
            }

            activeNotification(
                'Nie znaleziono wiadomości',
                'Wyszukiwanie historii nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return null;
        });
};

const getConversationDetails = async (conversationUuid: string): Promise<ChatDetailDto | null> => {
    return axiosApiInstance
        .get(`/chatbot/message/${conversationUuid}`)
        .then((resp) => {
            if (resp.data) {
                return resp.data;
            }

            return null;
        })
        .catch(() => {
            activeNotification(
                'Nie znaleziono konwersacji',
                'Wyszukiwanie szczegółów konwersacji nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return null;
        });
};

const deleteConversation = async (conversationUuid: string): Promise<boolean> => {
    return axiosApiInstance
        .delete(`/chatbot/message/delete/${conversationUuid}`)
        .then((resp) => {
            if (resp.status === 200) {
                activeNotification(
                    'Usunięto konwersację',
                    'Usunięcie konwersacji przebiegło pomyślnie.',
                    'success'
                );

                return true;
            }

            return false;
        })
        .catch(() => {
            activeNotification(
                'Nie udało się usunąć konwersacji',
                'Usuwanie konwersacji nie powiodło się, spróbuj jeszcze raz.',
                'warning'
            );

            return false;
        });
};

const streamAnswer = (
    chatbotMessage: ChatBotMessageDto,
    onChunk: (chunk: string) => void,
    onDone: (conversationUuid?: string, lastBotMessage?: MessageResponseDto) => void,
    onError: (errorMessage: string, errorStatus?: number) => void
): (() => void) => {
    let conversationUuid: string | undefined;
    const ctrl = new AbortController();

    const baseUrl = (axiosApiInstance.defaults.baseURL ?? '').replace(/\/$/, '');
    const authHeader = axiosApiInstance.defaults.headers.common['Authorization'];
    const token = authHeader ? String(authHeader) : undefined;

    const params = new URLSearchParams();
    params.set('message', chatbotMessage.message);

    if (chatbotMessage.conversationUuid) {
        params.set('conversationUuid', chatbotMessage.conversationUuid);
    }

    if (chatbotMessage.wantsAudioResponse) {
        params.set('wantsAudioResponse', 'true');
    }

    if (chatbotMessage.adContext) {
        params.set('adContext.adId', String(chatbotMessage.adContext.adId));
        params.set('adContext.adModule', chatbotMessage.adContext.adModule ?? '');
    }

    fetchEventSource(`${baseUrl}/chatbot/generate/stream?${params.toString()}`, {
        method: 'GET',
        headers: {
            ...token ? { Authorization: token } : {}
        },
        signal: ctrl.signal,
        openWhenHidden: true,

        async onopen (response) {
            if (!response.ok) {
                onError(
                    response.status === 423
                        ? 'Miesięczny limit tokenów został przekroczony. Spróbuj ponownie w następnym miesiącu lub dokup tokeny.'
                        : 'Wystąpił błąd podczas generowania odpowiedzi. Spróbuj ponownie później.',
                    response.status
                );
                throw new Error(`HTTP ${response.status}`);
            }
        },

        onmessage (event) {
            const state = { conversationUuid };
            const result = parseSSELine(`data: ${event.data}`, onChunk, state);

            conversationUuid = state.conversationUuid;

            if (result.done) {
                ctrl.abort();
                onDone(conversationUuid, result.lastBotMessage);
            }
        },

        onerror (err) {
            if (err.name !== 'AbortError') {
                onError('Wystąpił błąd podczas generowania odpowiedzi. Spróbuj ponownie później.');
            }
            throw err;
        }
    });

    return () => ctrl.abort();
};

const parseSSELine = (
    line: string,
    onChunk: (chunk: string) => void,
    state: { conversationUuid?: string; lastBotMessageId?: number }
): { done: boolean; lastBotMessage?: MessageResponseDto } => {
    if (!line.startsWith('data:')) return { done: false };

    const data = line.slice(5).trim();

    if (!data) return { done: false };

    try {
        const parsed = JSON.parse(data);

        if (parsed.type === 'CHUNK' && parsed.content != null) {
            onChunk(parsed.content);

            return { done: false };
        }

        if (parsed.type === 'DONE' || parsed.type === 'FILTER_DONE') {
            if (parsed.chatDetail?.conversationUuid) {
                state.conversationUuid = parsed.chatDetail.conversationUuid;
            }

            const botMessages = parsed.chatDetail?.messages?.filter(
                (m: MessageResponseDto) => m.messageOwner === 'BOT_MESSAGE'
            );
            const lastBotMessage = botMessages?.[botMessages.length - 1];

            if (lastBotMessage?.id) {
                state.lastBotMessageId = lastBotMessage.id;
            }

            return { done: true, lastBotMessage };
        }
    } catch {
        onChunk(data);
    }

    return { done: false };
};

const streamAnswerFromAudio = (
    audioBlob: Blob,
    chatbotMessage: ChatBotMessageDto,
    onChunk: (chunk: string) => void,
    onDone: (conversationUuid?: string, lastBotMessage?: MessageResponseDto) => void,
    onError: (errorMessage: string, errorStatus?: number) => void
): (() => void) => {
    const ctrl = new AbortController();
    const state: { conversationUuid?: string; lastBotMessageId?: number } = {};

    const baseUrl = (axiosApiInstance.defaults.baseURL ?? '').replace(/\/$/, '');
    const authHeader = axiosApiInstance.defaults.headers.common['Authorization'];
    const token = authHeader ? String(authHeader) : undefined;

    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice.webm');

    const jsonBlob = new Blob([JSON.stringify(chatbotMessage)], { type: 'application/json' });
    formData.append('data', jsonBlob);

    (async () => {
        try {
            const response = await fetch(`${baseUrl}/chatbot/generate/stream/audio`, {
                method: 'POST',
                headers: {
                    Accept: 'text/event-stream',
                    ...token ? { Authorization: token } : {}
                },
                body: formData,
                signal: ctrl.signal
            });

            if (!response.ok) {
                onError(
                    response.status === 423
                        ? 'Miesięczny limit tokenów został przekroczony. Spróbuj ponownie w następnym miesiącu lub dokup tokeny.'
                        : 'Wystąpił błąd podczas generowania odpowiedzi. Spróbuj ponownie później.',
                    response.status
                );

                return;
            }

            if (!response.body) {
                onError('Wystąpił błąd podczas generowania odpowiedzi. Spróbuj ponownie później.');

                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            let lastBotMessage: MessageResponseDto | undefined;

            while (true) {
                const { value, done } = await reader.read();

                if (value) {
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() ?? '';

                    for (const line of lines) {
                        const result = parseSSELine(line.trim(), onChunk, state);

                        if (result.done) {
                            lastBotMessage = result.lastBotMessage;
                        }
                    }
                }

                if (done) {
                    if (buffer.trim()) {
                        const result = parseSSELine(buffer.trim(), onChunk, state);

                        if (result.lastBotMessage) {
                            lastBotMessage = result.lastBotMessage;
                        }
                    }

                    break;
                }
            }

            onDone(state.conversationUuid, lastBotMessage);
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                onError('Wystąpił błąd podczas generowania odpowiedzi. Spróbuj ponownie później.');
            }
        }
    })();

    return () => ctrl.abort();
};

const getMessageAudio = async (messageId: number): Promise<string | null> => {
    try {
        const resp = await axiosApiInstance.get(`/chatbot/message/${messageId}/audio`, { responseType: 'blob' });

        return URL.createObjectURL(resp.data as Blob);
    } catch {
        try {
            await axiosApiInstance.post(`/chatbot/message/${messageId}/audio/generate`);
            const resp = await axiosApiInstance.get(`/chatbot/message/${messageId}/audio`, { responseType: 'blob' });

            return URL.createObjectURL(resp.data as Blob);
        } catch {
            return null;
        }
    }
};

const getSuggestedQuestions = async (data: SuggestedQuestionRequestDto): Promise<string[] | null> => {
    return axiosApiInstance
        .post('/chatbot/questions/suggested', data)
        .then((resp) => {
            const result: SuggestedQuestionCategoryDto = resp.data;

            return result.questions || null;
        })
        .catch(() => null);
};

export const chatbotApiRequests = {
    streamAnswer,
    streamAnswerFromAudio,
    getMessageAudio,
    getConversationHistory,
    getConversationDetails,
    deleteConversation,
    getSuggestedQuestions
};
